// Resumen ejecutivo de soporte (Req 5): KPIs de un periodo para dirección,
// con selector de periodo (mes / trimestre / personalizado) y descarga a PDF
// con branding Emprenet. Los datos los calcula el server (/api/stats/ejecutivo);
// aquí solo se pintan. Solo lo ven roles admin/soporte (gating en Estadisticas).
// Qué es cada número (📟, n=, hist., % de SLA) lo explica la Ayuda de
// Estadísticas (`components/ayuda/vistas/estadisticas.tsx`), sin tooltips.
import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  api,
  type NarrativaEjec,
  type ResumenEjecutivo as Resumen,
  type TiempoFlujo,
} from "@/lib/api";
import TextoConTE from "@/components/TextoConTE";
import { isAdmin, useAuth } from "@/lib/auth";

type Periodo = "mes" | "trimestre" | "custom";

const PERIODOS: { val: Periodo; label: string }[] = [
  { val: "mes", label: "Este mes" },
  { val: "trimestre", label: "Trimestre" },
  { val: "custom", label: "Personalizado" },
];

function fmtMin(m: number): string {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm === 0 ? `${h} h` : `${h} h ${mm} min`;
}

function fmtFecha(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function rango(
  tipo: Periodo,
  cDesde: string,
  cHasta: string,
): { desde: string; hasta: string } {
  const hoy = new Date();
  if (tipo === "mes") {
    return {
      desde: fmtFecha(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
      hasta: fmtFecha(hoy),
    };
  }
  if (tipo === "trimestre") {
    return {
      desde: fmtFecha(new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1)),
      hasta: fmtFecha(hoy),
    };
  }
  return { desde: cDesde, hasta: cHasta };
}

function tendencia(k: {
  actual: number;
  anterior: number;
  cambio_pct: number | null;
}): string {
  if (k.cambio_pct === null) return `primer periodo con datos (antes: ${k.anterior})`;
  if (k.cambio_pct === 0) return `sin cambio vs ${k.anterior} anteriores`;
  const flecha = k.cambio_pct > 0 ? "▲" : "▼";
  return `${flecha} ${Math.abs(k.cambio_pct)}% vs ${k.anterior} anteriores`;
}

export default function ResumenEjecutivo() {
  const me = useAuth((s) => s.user);
  const admin = isAdmin(me);
  const [tipo, setTipo] = useState<Periodo>("mes");
  const [cDesde, setCDesde] = useState("");
  const [cHasta, setCHasta] = useState("");
  const [data, setData] = useState<Resumen | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [narrativa, setNarrativa] = useState<NarrativaEjec | null>(null);
  const [generandoIA, setGenerandoIA] = useState(false);
  const [errorIA, setErrorIA] = useState<string | null>(null);

  const { desde, hasta } = useMemo(
    () => rango(tipo, cDesde, cHasta),
    [tipo, cDesde, cHasta],
  );
  const query = desde && hasta ? `?desde=${desde}&hasta=${hasta}` : "";

  useEffect(() => {
    if (tipo === "custom" && (!desde || !hasta)) return;
    setLoading(true);
    setError(null);
    api
      .get<Resumen>(`/api/stats/ejecutivo${query}`)
      .then(setData)
      .catch((e) =>
        setError(e instanceof ApiError ? e.detail : "No pude cargar el resumen"),
      )
      .finally(() => setLoading(false));
  }, [tipo, desde, hasta, query]);

  // Narrativa con IA cacheada (solo admin).
  useEffect(() => {
    if (!admin) return;
    if (tipo === "custom" && (!desde || !hasta)) return;
    setNarrativa(null);
    setErrorIA(null);
    api
      .get<NarrativaEjec>(`/api/stats/ejecutivo/narrativa${query}`)
      .then(setNarrativa)
      .catch(() => {});
  }, [admin, tipo, desde, hasta, query]);

  async function generarIA() {
    setGenerandoIA(true);
    setErrorIA(null);
    try {
      const n = await api.post<NarrativaEjec>(
        `/api/stats/ejecutivo/narrativa${query}`,
      );
      setNarrativa(n);
    } catch (e) {
      setErrorIA(e instanceof ApiError ? e.detail : "No se pudo generar");
    } finally {
      setGenerandoIA(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-1.5">
          {PERIODOS.map((p) => (
            <button
              key={p.val}
              type="button"
              onClick={() => setTipo(p.val)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                tipo === p.val
                  ? "bg-accent-500/15 text-accent-300 border-accent-500/40"
                  : "border-ink-600 text-zinc-400 hover:text-white"
              }`}
            >
              {p.label}
            </button>
          ))}
          {tipo === "custom" && (
            <span className="flex flex-wrap items-center gap-1.5">
              <input
                type="date"
                className="input text-xs py-1"
                value={cDesde}
                max={cHasta || undefined}
                onChange={(e) => setCDesde(e.target.value)}
              />
              <span className="text-zinc-500 text-xs">a</span>
              <input
                type="date"
                className="input text-xs py-1"
                value={cHasta}
                min={cDesde || undefined}
                onChange={(e) => setCHasta(e.target.value)}
              />
            </span>
          )}
        </div>
        {data && admin && (
          <a
            href={`/api/stats/ejecutivo/pdf${query}`}
            target="_blank"
            rel="noopener"
            className="btn-ghost text-sm border border-ink-600 w-full sm:w-auto text-center"
          >
            ⬇ Descargar PDF
          </a>
        )}
      </div>

      {error ? (
        <div className="card text-sm text-red-300">{error}</div>
      ) : loading || !data ? (
        <div className="card text-sm text-zinc-500">Cargando resumen…</div>
      ) : (
        <>
          <p className="text-xs text-zinc-500">
            Periodo: <span className="text-zinc-300">{data.periodo_label}</span>
          </p>

          {data.hallazgos.length > 0 && (
            <div className="card">
              <h3 className="text-xs uppercase tracking-wide text-zinc-400 mb-2">
                Lo que dicen los números
              </h3>
              <ul className="space-y-1.5 text-sm text-zinc-300 list-disc list-inside">
                {data.hallazgos.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </div>
          )}

          {admin && (
            <div className="card space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-xs uppercase tracking-wide text-zinc-400">
                  Análisis con IA
                </h3>
                <button
                  type="button"
                  onClick={generarIA}
                  disabled={generandoIA}
                  className="btn-ghost text-xs border border-ink-600 disabled:opacity-50"
                >
                  {generandoIA
                    ? "Generando…"
                    : narrativa?.texto
                      ? "↻ Regenerar"
                      : "✨ Generar análisis con IA"}
                </button>
              </div>
              {errorIA && <p className="text-xs text-red-300">{errorIA}</p>}
              {narrativa?.texto ? (
                <>
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">
                    <TextoConTE texto={narrativa.texto} />
                  </p>
                  {narrativa.generado_at && (
                    <p className="text-[10px] text-zinc-500">
                      Generado
                      {narrativa.generado_por ? ` por ${narrativa.generado_por}` : ""} ·{" "}
                      {new Date(narrativa.generado_at).toLocaleString()}
                    </p>
                  )}
                </>
              ) : (
                !generandoIA && (
                  <p className="text-xs text-zinc-500">
                    Redacta un análisis ejecutivo (situación, implicaciones y
                    recomendaciones) a partir de los datos del periodo. Bajo
                    demanda; se guarda para este periodo y sale en el PDF.
                  </p>
                )
              )}
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi
              label="Casos creados"
              value={data.creados.actual}
              meta={tendencia(data.creados)}
            />
            <Kpi
              label="Casos cerrados"
              value={data.cerrados.actual}
              meta={tendencia(data.cerrados)}
            />
            <Kpi
              label="Abiertos hoy"
              value={data.abiertos_actuales}
              meta={`Antigüedad prom.: ${data.aging_promedio_dias ?? "—"} d${
                data.backlog_dias_para_limpiar != null
                  ? ` · ~${data.backlog_dias_para_limpiar} d para vaciarlo al ritmo actual`
                  : ""
              }`}
            />
            <Kpi
              label="Resolución"
              value={`${data.resolucion_promedio_dias ?? "—"} d`}
              meta={
                data.resolucion_n
                  ? `Mediana ${data.resolucion_mediana_dias ?? "—"} d · ${data.resolucion_n} cerrado(s)${
                      data.resolucion_cliente_n
                        ? ` · desde reporte del cliente: ${data.resolucion_cliente_promedio_dias ?? "—"} d prom`
                        : ""
                    }`
                  : "Sin cierres en el periodo"
              }
            />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Lista
              titulo="Casos por categoría"
              filas={data.por_categoria}
              vacio="Sin casos creados en el periodo."
            />
            <Lista
              titulo="Abiertos por estado (hoy)"
              filas={data.por_estado}
              vacio="Sin casos abiertos."
            />
            <Lista
              titulo="Empresas con más soporte"
              filas={data.top_empresas}
              vacio="Sin casos creados en el periodo."
            />
            <Lista
              titulo="Modelos con más soporte"
              filas={data.top_modelos}
              vacio="Sin casos creados en el periodo."
            />
          </div>

          {(data.flujo_por_posesion.length > 0 ||
            data.flujo_por_estado.length > 0 ||
            data.sla_cumplimiento.length > 0) && (
            <div className="card space-y-4">
              <div>
                <h3 className="text-xs uppercase tracking-wide text-zinc-400">
                  Flujo · ¿dónde se va el tiempo?
                </h3>
                <p className="text-[11px] text-zinc-600">
                  Tiempo retrospectivo en los casos cerrados del periodo
                  (reconstruido desde la bitácora).
                </p>
              </div>

              {data.flujo_por_posesion.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Tiempo con la pelota
                  </h4>
                  <BarrasFlujo filas={data.flujo_por_posesion} />
                </div>
              )}

              {data.responsividad.length > 0 && (
                <div className="space-y-2 border-t border-ink-700 pt-3">
                  <h4 className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Responsividad — cuánto tardan ellos en responder
                  </h4>
                  <BarrasFlujo filas={data.responsividad} mostrarSinDatos />
                </div>
              )}

              {data.flujo_por_estado.length > 0 && (
                <div className="space-y-2 border-t border-ink-700 pt-3">
                  <h4 className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Tiempo por estado
                  </h4>
                  <BarrasFlujo filas={data.flujo_por_estado} />
                </div>
              )}

              {data.sla_cumplimiento.length > 0 && (
                <div className="space-y-2 border-t border-ink-700 pt-3">
                  <h4 className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Cumplimiento de SLA por estado
                  </h4>
                  <ul className="space-y-1">
                    {data.sla_cumplimiento.map((s) => (
                      <li
                        key={s.estado}
                        className="flex items-baseline justify-between gap-2 text-xs"
                      >
                        <span className="text-zinc-300 break-words min-w-0">
                          {s.etiqueta}{" "}
                          <span className="text-zinc-600">
                            ≤ {s.umbral_dias} d
                          </span>
                        </span>
                        <span className="font-mono shrink-0">
                          <span className={pctColor(s.pct)}>{s.pct ?? "—"}%</span>{" "}
                          <span className="text-zinc-600">
                            ({s.dentro}/{s.casos})
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {data.config_equipo && (
            <div className="card space-y-2 border-emerald-500/20">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-xs uppercase tracking-wide text-zinc-400">
                    ⏱ Tiempo configurando equipos
                  </h3>
                  <p className="text-[11px] text-zinc-600">
                    Recurso del equipo de soporte en el periodo · {data.config_equipo.equipos}{" "}
                    equipo{data.config_equipo.equipos === 1 ? "" : "s"} ·{" "}
                    {data.config_equipo.casos} caso{data.config_equipo.casos === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="text-lg font-semibold text-emerald-300">
                  {fmtMin(data.config_equipo.total_min)}
                </div>
              </div>
              {data.config_equipo.por_tecnico.length > 0 && (
                <ul className="space-y-1 border-t border-ink-700 pt-2">
                  {data.config_equipo.por_tecnico.map((t, i) => (
                    <li
                      key={i}
                      className="flex items-baseline justify-between gap-2 text-xs"
                    >
                      <span className="text-zinc-300 break-words min-w-0">{t.nombre}</span>
                      <span className="font-mono text-zinc-400 shrink-0">
                        <span className="text-emerald-200">{fmtMin(t.minutos)}</span>{" "}
                        <span className="text-zinc-600">({t.sesiones} ses.)</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {data.fallas_recurrentes.length > 0 && (
            <div className="card space-y-2">
              <h3 className="text-xs uppercase tracking-wide text-zinc-400">
                Fallas recurrentes por modelo
              </h3>
              <p className="text-[11px] text-zinc-600">
                Del periodo · el histórico es el acumulado de siempre.
              </p>
              <ul className="space-y-1">
                {data.fallas_recurrentes.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-baseline justify-between gap-2 text-xs"
                  >
                    <span className="text-zinc-300 break-words min-w-0">
                      {f.modelo} — {f.falla}
                    </span>
                    <span className="font-mono text-zinc-400 shrink-0">
                      <span
                        className="text-accent-300"
                        aria-label={`${f.equipos} equipo${f.equipos === 1 ? "" : "s"}`}
                      >
                        {f.equipos}📟
                      </span>{" "}
                      {f.casos} caso{f.casos === 1 ? "" : "s"}
                      {f.casos_historico != null &&
                        f.casos_historico !== f.casos && (
                          <span className="text-zinc-600">
                            {" "}
                            · {f.casos_historico} hist.
                          </span>
                        )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.soluciones.length > 0 && (
            <div className="card space-y-2">
              <h3 className="text-xs uppercase tracking-wide text-zinc-400">
                ¿Cómo se resuelven?
              </h3>
              <ul className="space-y-1">
                {data.soluciones.map((s, i) => (
                  <li
                    key={i}
                    className="flex items-baseline justify-between gap-2 text-xs"
                  >
                    <span className="text-zinc-300 break-words min-w-0">
                      {s.solucion}
                    </span>
                    <span className="font-mono text-zinc-400 shrink-0">
                      <span
                        className="text-accent-300"
                        aria-label={`${s.equipos} equipo${s.equipos === 1 ? "" : "s"}`}
                      >
                        {s.equipos}📟
                      </span>{" "}
                      {s.casos} caso{s.casos === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  meta,
}: {
  label: string;
  value: number | string;
  meta: string;
}) {
  return (
    <div className="card">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">
        {label}
      </div>
      <div className="text-2xl font-mono text-zinc-100">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{meta}</div>
    </div>
  );
}

function Lista({
  titulo,
  filas,
  vacio,
}: {
  titulo: string;
  filas: { etiqueta: string; casos: number; equipos?: number | null }[];
  vacio: string;
}) {
  return (
    <div className="card space-y-2">
      <h3 className="text-xs uppercase tracking-wide text-zinc-400">{titulo}</h3>
      {filas.length === 0 ? (
        <p className="text-xs text-zinc-600">{vacio}</p>
      ) : (
        <ul className="space-y-1">
          {filas.map((f) => (
            <li
              key={f.etiqueta}
              className="flex items-baseline justify-between gap-2 text-xs"
            >
              <span className="text-zinc-300 break-words min-w-0">{f.etiqueta}</span>
              <span className="font-mono text-zinc-400 shrink-0">
                {f.equipos != null && (
                  <span
                    className="text-accent-300"
                    aria-label={`${f.equipos} equipo${f.equipos === 1 ? "" : "s"}`}
                  >
                    {f.equipos}📟{" "}
                  </span>
                )}
                {f.casos}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function fmtDur(f: TiempoFlujo): string {
  const d = f.dias_promedio;
  if (d == null) return "—";
  if (d < 1 && f.horas_promedio != null) return `${f.horas_promedio} h`;
  return `${d} d`;
}

function pctColor(pct: number | null): string {
  if (pct == null) return "text-zinc-400";
  if (pct >= 90) return "text-emerald-300";
  if (pct >= 70) return "text-amber-300";
  return "text-red-300";
}

function BarrasFlujo({
  filas,
  mostrarSinDatos = false,
}: {
  filas: TiempoFlujo[];
  mostrarSinDatos?: boolean;
}) {
  const max = Math.max(1, ...filas.map((f) => f.dias_promedio ?? 0));
  return (
    <ul className="space-y-1.5">
      {filas.map((f) => {
        if (f.dias_promedio == null) {
          if (!mostrarSinDatos) return null;
          return (
            <li
              key={f.clave}
              className="flex items-baseline justify-between gap-2 text-xs"
            >
              <span className="text-zinc-300">{f.etiqueta}</span>
              <span className="text-zinc-600">sin datos</span>
            </li>
          );
        }
        const ancho = Math.max(3, Math.round((f.dias_promedio / max) * 100));
        return (
          <li key={f.clave} className="text-xs">
            <div className="flex items-baseline justify-between gap-2 mb-0.5">
              <span className="text-zinc-300 break-words min-w-0">
                {f.etiqueta}
              </span>
              <span className="font-mono text-zinc-400 shrink-0">
                {fmtDur(f)} <span className="text-zinc-600">· n={f.n}</span>
              </span>
            </div>
            <div className="h-1.5 rounded bg-ink-800 overflow-hidden">
              <div
                className="h-full bg-accent-500/60"
                style={{ width: `${ancho}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
